import { useMutation, useQuery } from '@apollo/client';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import {
  ItemAvailability,
  type ContentCategoriesQuery,
  type DeleteItemMutation,
  type DeleteItemMutationVariables,
} from '../../../../../../__gqltypes__';
import { LoadingLogo } from '../../../../devtools/components/Modal';
import { DELETE_ITEM, GET_CONTENT_CATEGORIES, UPDATE_ITEM } from '../../../components/apolloQueries';
import OwnershipStatsCard, { VanityCategory } from '../../../../devtools/screens/VanityItemTools/OwnershipStatsCard';
import { CURRENCY_TYPES, getQuery } from '../utils';
import DeleteVanityModal from './DeleteVanityModal';
import ItemForm from './ItemForm';
import { Item, ItemsRefetch } from './typedef';
import UpgradeForm from './UpgradeForm';
import VinylEditor from './VinylEditor';
import VinylStoredAssetsCard from '../vinylTheme/VinylStoredAssetsCard';

const TYPE_TO_VANITY_CATEGORY: Record<string, VanityCategory> = {
  Frame: 'PROFILE_FRAME',
  Sticker: 'STICKER',
  Vinyl: 'APP_SKIN',
};

type Props = {
  item: Item;
  type: string;
  refetch: ItemsRefetch;
};

export default function ItemView({ item, type, refetch }: Props) {
  const [itemData, setItemData] = useState(item);
  const { data } = useQuery<ContentCategoriesQuery>(GET_CONTENT_CATEGORIES);
  const isVinylItem = item?.__typename === 'AppSkin';
  // Frame/Sticker keep the original behavior: resync the local form on every item change ([item]).
  // Vinyl only resyncs when the id changes ([item?.id]) so a same-item refetch (e.g. after an asset
  // upload) doesn't wipe its multi-step unsaved edits (name, folder, auto-filled theme assets, levels).
  useEffect(() => {
    setItemData(item);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVinylItem ? item?.id : item]);
  const [updateItem, { loading: updateLoading }] = useMutation(UPDATE_ITEM, {
    onCompleted: () => {
      refetch();
      // Vinyl doesn't resync from a same-item refetch, so a saved new level would keep its client-only
      // isNewUpgrade marker (stuck on the "save first" placeholder). Clear it here. Frame/Sticker get
      // this for free from the [item] resync above, so we leave their path untouched.
      if (isVinylItem) {
        setItemData((prev) =>
          prev
            ? {
                ...prev,
                // @ts-ignore - isNewUpgrade is a client-only marker not on the Item type
                nextLevelItems: (prev.nextLevelItems ?? []).map((u) => ({ ...u, isNewUpgrade: false })),
              }
            : prev
        );
      }
    },
  });
  const history = useHistory();
  const match = useRouteMatch();
  // 'idle' = no modal, 'warn' = extra warning for live items, 'confirm' = final confirmation
  const [deleteStep, setDeleteStep] = useState<'idle' | 'warn' | 'confirm'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteItem, { loading: deleteLoading }] = useMutation<DeleteItemMutation, DeleteItemMutationVariables>(
    DELETE_ITEM,
    { onCompleted: () => refetch() }
  );

  // an item that is not UNAVAILABLE may be owned by / visible to players, so require a double confirmation
  const isUnavailable = itemData?.liveOpsAvailability === ItemAvailability.UNAVAILABLE;

  const handleDeleteClick = () => setDeleteStep(isUnavailable ? 'confirm' : 'warn');

  const handleDelete = () => {
    deleteItem({ variables: { input: { id: itemData.id } } });
    setDeleteStep('idle');
    // navigate back to the list; ItemsView redirects to the first remaining item
    history.push(match.url.slice(0, match.url.lastIndexOf('/')));
  };

  const handleUpdate = () => {
    // Vinyl only: a purchasable vinyl must have a real price on every level. Auto-created theme levels
    // start at 0, so block the save (rather than silently persisting a free/broken level) and say which.
    if (itemData.__typename === 'AppSkin' && itemData.cost) {
      const isBadAmount = (amount: unknown) => {
        const n =
          amount === '' || amount === null || amount === undefined
            ? NaN
            : typeof amount === 'number'
            ? amount
            : parseInt(String(amount), 10);
        return Number.isNaN(n) || n <= 0;
      };
      const missing: string[] = [];
      // @ts-ignore - cost is loosely typed across the Item union
      if (isBadAmount(itemData.cost.amount)) missing.push('Base item');
      // @ts-ignore
      (itemData.nextLevelItems ?? []).forEach((u) => {
        // @ts-ignore
        if (isBadAmount(u.cost?.amount)) missing.push(`Item level ${u.level}`);
      });
      if (missing.length) {
        setSaveError(`Set a price greater than 0 before saving: ${missing.join(', ')}.`);
        return;
      }
    }
    setSaveError(null);
    const itemCost = itemData.cost
      ? {
          // @ts-ignore
          amount: itemData.cost.amount === '' ? 0 : parseInt(itemData.cost.amount, 10),
          // @ts-ignore
          type: Object.keys(CURRENCY_TYPES).find((key) => CURRENCY_TYPES[key] === itemData.cost.__typename),
          // @ts-ignore
          category: itemData.cost.category ? itemData.cost.category.id : null,
        }
      : null;
    const levelItems =
      itemData.nextLevelItems && itemData.nextLevelItems.length
        ? // @ts-ignore
          itemData.nextLevelItems.map(({ asset, cost }) => ({
            asset,
            cost: {
              // @ts-ignore
              amount: cost?.amount === '' || cost === null ? 0 : parseInt(cost.amount, 10),
              type: itemCost ? itemCost.type : 'CURRENCY_DIAMOND',
            },
          }))
        : null;
    updateItem({
      variables: {
        input: {
          id: itemData.id,
          name: itemData.name,
          cost: itemCost,
          asset: itemData.asset,
          type: getQuery(type).typeQ,
          availability: itemData.liveOpsAvailability,
          levelItems,
          // @ts-ignore
          iconAsset: itemData.iconAsset || '',
          // @ts-ignore
          iconPrimaryColor: itemData.iconPrimaryColor || '',
          // @ts-ignore
          iconBackgroundColor: itemData.iconBackgroundColor || '',
        },
      },
    });
  };

  const handleAddLevel = () => {
    const { nextLevelItems } = itemData;
    nextLevelItems.push({
      id: `${itemData.id}.${nextLevelItems.length + 1}`,
      level: nextLevelItems.length + 2,
      // @ts-ignore
      cost: { amount: 0 },
      asset: '',
      isNewUpgrade: true,
    });
    setItemData({ ...itemData, nextLevelItems });
  };

  const handleDeleteLevel = () => {
    const { nextLevelItems } = itemData;
    nextLevelItems.pop();
    setItemData({ ...itemData, nextLevelItems });
  };

  const iterate = (root: Record<string, string> | string, acc: string[]) => {
    if (_.isString(root)) {
      if (root.startsWith('https')) {
        acc.push(root);
      }
      return;
    }
    if (_.isArray(root)) {
      root.forEach((r) => iterate(r, acc));
      return;
    }

    if (!_.isObject(root)) {
      return;
    }

    const val = Object.values(root);
    val.forEach((v) => iterate(v, acc));
  };

  const handleValidateJsonItem = (f: Blob): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (evt) => {
        let jsonObj = {};
        const validationErrors = [];
        try {
          // @ts-ignore
          jsonObj = JSON.parse(evt.target.result);
        } catch (e) {
          console.error('not valid JSON file', e);
          validationErrors.push('not valid JSON file');
        }

        const acc: string[] = [];
        iterate(jsonObj, acc);
        for (const url of acc) {
          try {
            const response = await fetch(url);
            if (response.status !== 200) {
              validationErrors.push(`${url} not found`);
            }
          } catch (e) {
            validationErrors.push(`${url} not found`);
          }
        }
        resolve(validationErrors);
      };

      reader.readAsText(f);
    });
  };

  // itemData can briefly be undefined right after a delete, before the list redirects away
  if (!itemData) return null;

  const { contentCategories } = data ? data.app : { contentCategories: [] };
  const isVinyl = itemData.__typename === 'AppSkin';

  return (
    <>
      <Card className="mt-3">
        <LoadingLogo show={updateLoading || deleteLoading} />
        {/* Extra step for live items: warn before reaching the final confirmation */}
        <DeleteVanityModal
          type={type}
          name={itemData.name}
          warning={`⚠️ This ${type} is "${itemData.liveOpsAvailability}", not UNAVAILABLE — players may already own or see it. Deletion is permanent and cannot be undone: every player who owns it will be stuck with a broken item that can never be restored.`}
          confirmLabel="I understand, continue"
          show={deleteStep === 'warn' && !deleteLoading}
          onHide={() => setDeleteStep('idle')}
          onConfirm={() => setDeleteStep('confirm')}
        />
        {/* Final confirmation (single step for UNAVAILABLE items) */}
        <DeleteVanityModal
          type={type}
          name={itemData.name}
          show={deleteStep === 'confirm' && !deleteLoading}
          onHide={() => setDeleteStep('idle')}
          onConfirm={handleDelete}
        />
        <Card.Header className="text-center d-flex justify-content-between">
          <h4>{itemData.name}</h4>
          <div>
            <Button className="mr-3" variant="danger" onClick={handleDeleteClick}>
              Delete
            </Button>
            <Button onClick={() => handleUpdate()}>Save</Button>
          </div>
        </Card.Header>
        <Card.Body style={{ height: '100%' }}>
          {saveError && (
            <Alert variant="danger" onClose={() => setSaveError(null)} dismissible>
              {saveError}
            </Alert>
          )}
          {isVinyl ? (
            <VinylEditor
              itemData={itemData}
              setItemData={setItemData}
              categories={contentCategories}
              refetch={refetch}
              onValidate={handleValidateJsonItem}
              onAddLevel={handleAddLevel}
              onRemoveLevel={handleDeleteLevel}
            />
          ) : (
            <>
              <ItemForm
                itemData={itemData}
                setItemData={setItemData}
                categories={contentCategories}
                refetch={refetch}
                onValidate={undefined}
              />
              {itemData.nextLevelItems.map((upgrade) => (
                <UpgradeForm
                  upgrade={upgrade}
                  itemData={itemData}
                  setItemData={setItemData}
                  refetch={refetch}
                  key={upgrade.id}
                  // @ts-ignore
                  isNewUpgrade={upgrade.isNewUpgrade}
                  onValidate={undefined}
                />
              ))}
              <br />
              <div className="d-flex justify-content-between">
                <Button variant="success" onClick={handleAddLevel}>
                  <i className="fas fa-plus-circle mr-2" />
                  Add an upgrade
                </Button>
                <Button variant="danger" onClick={handleDeleteLevel}>
                  <i className="fas fa-minus-circle mr-2" /> Remove an upgrade
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
      {isVinyl && <VinylStoredAssetsCard key={itemData.id} itemId={itemData.id} />}
      {TYPE_TO_VANITY_CATEGORY[type] && (
        <OwnershipStatsCard category={TYPE_TO_VANITY_CATEGORY[type]} itemId={itemData.id} />
      )}
    </>
  );
}
